import {
  Box,
  Heading,
  Text,
  HStack,
  Grid,
  Button,
  VStack,
} from "@chakra-ui/react";
import { motion } from "framer-motion";

const MotionBox = motion(Box);
const MotionHeading = motion(Heading);

export default function AboutPage({ onOpenModal }) {
  return (
    <Box
      id="about"
      py={{ base: 24, md: 36 }}
      px={{ base: 6, md: 12, lg: 20 }}
      bg="brand.black"
    >
      <Box maxW="1400px" mx="auto">
        {/* Label */}
        <MotionBox
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          mb={12}
        >
          <HStack spacing={3}>
            <Box w="32px" h="1px" bg="brand.coral" />
            <Text
              fontSize="xs"
              letterSpacing="0.3em"
              textTransform="uppercase"
              color="brand.coral"
              fontWeight="600"
            >
              About Us
            </Text>
          </HStack>
        </MotionBox>

        {/* Hero Statement */}
        <MotionHeading
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          fontFamily="'Playfair Display', serif"
          fontSize={{ base: "5xl", md: "7xl", lg: "9xl" }}
          fontWeight="400"
          color="brand.cream"
          lineHeight="1.0"
          mb={24}
          letterSpacing="-0.02em"
        >
          We Are
          <Text
            as="span"
            fontStyle="italic"
            color="brand.coral"
            display="block"
          >
            the Bridge.
          </Text>
        </MotionHeading>

        {/* Brand Story */}
        <Grid
          templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
          gap={20}
          mb={24}
        >
          <MotionBox
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Text
              color="rgba(245,240,235,0.7)"
              lineHeight="2"
              fontSize="md"
              mb={6}
            >
              Reliable Touch Services is a professional outreach and lead
              generation company specialising in the real estate sector. We act
              as the dedicated communication arm for estate agents, connecting
              them with motivated landlords, property owners, and qualified
              prospects.
            </Text>
            <Text
              color="rgba(245,240,235,0.7)"
              lineHeight="2"
              fontSize="md"
              mb={8}
            >
              Our team of skilled communicators understands the property
              landscape — the language, the urgency, and the nuance required to
              open doors that convert to real business.
            </Text>

            <Box
              borderLeft="2px solid"
              borderColor="brand.coral"
              pl={6}
              my={8}
            >
              <Text
                fontFamily="'Playfair Display', serif"
                fontSize={{ base: "lg", md: "xl" }}
                fontStyle="italic"
                color="brand.cream"
                lineHeight="1.8"
              >
                "By acting as a bridge between landlords and trusted real estate
                professionals, we create opportunities that save time and help
                the property industry operate more efficiently."
              </Text>
            </Box>
          </MotionBox>

          <MotionBox
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            {/* Vision */}
            <Box bg="brand.sand" p={{ base: 8, md: 10 }} mb={4}>
              <Text
                fontSize="xs"
                letterSpacing="0.3em"
                textTransform="uppercase"
                color="brand.coral"
                fontWeight="600"
                mb={4}
              >
                Our Vision
              </Text>
              <Text
                fontFamily="'Playfair Display', serif"
                fontSize={{ base: "md", md: "lg" }}
                color="brand.charcoal"
                lineHeight="1.8"
              >
                To become the trusted communication bridge between landlords and
                real estate professionals — creating opportunities, saving time,
                and helping the property industry operate more efficiently.
              </Text>
            </Box>

            {/* Mission */}
            <Box
              bg="brand.graphite"
              border="1px solid rgba(46,46,46,0.8)"
              p={{ base: 8, md: 10 }}
            >
              <Text
                fontSize="xs"
                letterSpacing="0.3em"
                textTransform="uppercase"
                color="brand.coral"
                fontWeight="600"
                mb={4}
              >
                Our Mission
              </Text>
              <Text
                color="rgba(245,240,235,0.7)"
                fontSize="md"
                lineHeight="1.9"
              >
                To empower real estate agents by handling the time-consuming
                process of outreach and lead generation — allowing them to focus
                on delivering excellent service and closing successful property
                transactions.
              </Text>
            </Box>
          </MotionBox>
        </Grid>

        {/* Differentiation Block */}
        <MotionBox
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          bg="brand.charcoal"
          p={{ base: 10, md: 16 }}
          position="relative"
          overflow="hidden"
          mb={20}
        >
          <Box
            position="absolute"
            top="0"
            right="0"
            w="300px"
            h="300px"
            background="radial-gradient(circle, rgba(224,92,58,0.08) 0%, transparent 70%)"
            pointerEvents="none"
          />
          <Text
            fontSize="xs"
            letterSpacing="0.3em"
            textTransform="uppercase"
            color="brand.coral"
            fontWeight="600"
            mb={4}
          >
            Why a Call Center?
          </Text>
          <Heading
            fontFamily="'Playfair Display', serif"
            fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
            fontWeight="400"
            color="brand.cream"
            mb={6}
            lineHeight="1.3"
            maxW="600px"
          >
            We aren't agents.
            <Text as="span" fontStyle="italic" color="brand.coral">
              {" "}
              We are your dedicated outreach team.
            </Text>
          </Heading>
          <Text
            color="rgba(245,240,235,0.6)"
            maxW="580px"
            lineHeight="1.9"
            fontSize="md"
          >
            While we specialise in real estate, our expertise is communication.
            This allows agents to stop multitasking and start specialising. We
            use proven scripts and data-driven strategies crafted specifically
            for the property sector — turning cold contacts into warm
            conversations, and conversations into closed deals.
          </Text>
        </MotionBox>

        {/* Final CTA */}
        <MotionBox
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          textAlign="center"
        >
          <Heading
            fontFamily="'Playfair Display', serif"
            fontSize={{ base: "2xl", md: "3xl" }}
            fontWeight="400"
            color="brand.cream"
            mb={6}
          >
            Meet the team behind
            <Text as="span" fontStyle="italic" color="brand.coral">
              {" "}
              your next deal.
            </Text>
          </Heading>
          <Button
            variant="coral"
            onClick={() =>
              document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
            }
          >
            Get In Touch
          </Button>
        </MotionBox>
      </Box>
    </Box>
  );
}
